import {ApiError, ErrorAlert, ProblemJson} from "@variocube/app-ui";
import React, {useEffect, useRef} from "react";
import {useLocalization} from "../i18n";

/**
 * Shows the booking-creation failure message with the technical error detail below.
 *
 * Reuses app-ui's {@link ErrorAlert} but overrides its headline with our localized, user-friendly message: we hand it a
 * synthetic RFC-7807 problem whose {@code title} is that message and whose {@code detail}/{@code status} carry the
 * technical information (from the real problem body). The technical text is intentionally not localized — per the issue,
 * printing it verbatim is fine.
 *
 * A sold-out size ({@code BOX_SIZE_UNAVAILABLE}, the lost side of a last-box race) is a *logical* outcome, not a
 * technical fault: it gets a self-explanatory localized message and no technical noise, mirroring {@code ProlongError}.
 *
 * Scrolls itself into view when the error appears: both checkout flows render this near the bottom of the page, which
 * can be outside the viewport when the user submits via the sticky nav.
 */
export function OrderSubmitError({error}: { error: unknown }) {
	const {t} = useLocalization();
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		ref.current?.scrollIntoView({behavior: "smooth", block: "center"});
	}, [error]);

	return (
		<ErrorAlert
			ref={ref}
			error={toProblem(error, {
				generic: t("orders.submitError"),
				boxSoldOut: t("orders.boxSoldOut"),
				bookingBusy: t("orders.bookingBusy"),
			})}
		/>
	);
}

/**
 * Wraps an arbitrary rejection into a problem whose headline is our friendly message, keeping the technical detail and
 * (where available) HTTP status for the smaller text below. A recognized logical error code replaces the headline with
 * its own message and drops the technical detail.
 */
function toProblem(
	error: unknown,
	messages: { generic: string; boxSoldOut: string; bookingBusy: string },
): Partial<ProblemJson> {
	const {generic: title} = messages;
	if (error instanceof ApiError) {
		if (error.code === "BOX_SIZE_UNAVAILABLE") {
			return {title: messages.boxSoldOut};
		}
		if (error.code === "BOOKING_BUSY") {
			return {title: messages.bookingBusy};
		}
		return {title, detail: error.detail ?? error.title, status: error.status};
	}
	if (error instanceof Error) {
		return {title, detail: error.message};
	}
	if (typeof error === "string") {
		return {title, detail: error};
	}
	return {title};
}
